import type { VerifyResponse } from './api';

export interface CertificateFields {
  nameLa: string;
  nameEn: string;
  taxCode: string;
}

export type CertificateResult =
  | { ok: true; fields: CertificateFields; stale: boolean }
  | { ok: false; reason: 'invalid_signature' | 'registry_error' | 'no_names' };

const text = (v: string | null | undefined) => (v ?? '').trim();

/** Decide what a verification response is allowed to put in the form.
 *
 *  Every refusal fills nothing at all: a partly-filled form is worse than an
 *  empty one, because the operator has no way to tell which fields came from the
 *  certificate and which were left over.
 */
export function certificateFields(res: VerifyResponse): CertificateResult {
  if (text(res.ErrorText) !== '') return { ok: false, reason: 'registry_error' };
  // The registry signs what it prints; without a valid signature this is not a
  // certificate it issued, whatever the rest of the payload says.
  if (res.IsSignatureValid !== true) return { ok: false, reason: 'invalid_signature' };

  const entity = res.CurrentData?.BusinessEntity;
  const nameLa = text(entity?.NameLao);
  const nameEn = text(entity?.NameEnglish);
  if (nameLa === '' || nameEn === '') return { ok: false, reason: 'no_names' };

  return {
    ok: true,
    fields: {
      nameLa,
      nameEn,
      taxCode: text(res.CurrentData?.RegistrationObjectInfo?.RegistrationObjectNumber),
    },
    // The certificate is a printout: the registry may have moved on since.
    stale: res.UpdatedDataExists === true,
  };
}
